<template>
  <div class="page-container">
    <el-card>
      <!-- 搜索区 -->
      <div class="search-bar">
        <el-input
          v-model="query.keyword"
          placeholder="搜索手机号/昵称"
          clearable
          style="width: 200px"
          @keyup.enter.native="handleSearch"
        />
        <el-checkbox v-model="query.pending_coupon_issue" border size="mini" @change="handleSearch">
          仅看待发放优惠券用户
        </el-checkbox>
        <el-button type="primary" size="mini" icon="el-icon-search" @click="handleSearch">查询</el-button>
        <el-button size="mini" icon="el-icon-refresh" @click="handleReset">重置</el-button>
      </div>

      <!-- 待发放提示横幅 -->
      <el-alert
        v-if="query.pending_coupon_issue"
        title="当前仅显示：已完成服务订单但尚未发放过优惠券的用户"
        type="warning"
        :closable="false"
        show-icon
        style="margin-bottom:12px"
      />

      <!-- 操作按钮 -->
      <div class="action-bar">
        <el-button
          v-if="memberConfig.allowCreate"
          v-permission="['member:create']"
          type="success"
          size="mini"
          icon="el-icon-plus"
          @click="handleCreate"
        >新建</el-button>
      </div>

      <!-- 表格 -->
      <el-table ref="table" :key="`member-table-${memberConfig.showBalance}-${memberConfig.showPoints}`" :data="tableData" v-loading="loading" border stripe style="width: 100%">
        <el-table-column prop="id" label="ID" width="70" align="center" />
        <el-table-column label="头像" width="70" align="center">
          <template slot-scope="{ row }">
            <el-avatar :size="36" :src="row.avatar || defaultAvatar" />
          </template>
        </el-table-column>
        <el-table-column prop="phone" label="手机号" width="130" />
        <el-table-column prop="nickname" label="昵称" width="130" />
        <el-table-column v-if="memberConfig.showBalance" label="余额" width="110" align="right">
          <template slot-scope="{ row }">
            <span class="balance-text">¥ {{ Number(row.balance).toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="memberConfig.showPoints" label="积分" width="100" align="right">
          <template slot-scope="{ row }">
            <span class="points-text">{{ row.points }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="注册时间" min-width="160" />
        <el-table-column label="绑定合伙人" min-width="160">
          <template slot-scope="{ row }">
            <template v-if="row.partner_name || row.partner_id">
              <div class="partner-info">
                <span class="partner-name">{{ row.partner_name || '—' }}</span>
                <span v-if="row.partner_phone" class="partner-phone">{{ row.partner_phone }}</span>
              </div>
            </template>
            <span v-else class="text-muted">未绑定</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="490" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              v-if="memberConfig.allowUpdate"
              v-permission="['member:update']"
              type="primary" size="mini" icon="el-icon-edit"
              @click="handleEdit(row)"
            >编辑</el-button>
            <el-button
              v-if="memberConfig.showBalance"
              v-permission="['member:balance']"
              type="success" size="mini" icon="el-icon-money"
              @click="handleBalance(row)"
            >余额</el-button>
            <el-button
              v-if="memberConfig.showPoints"
              v-permission="['member:points']"
              type="info" size="mini" icon="el-icon-star-off"
              @click="handlePoints(row)"
            >积分</el-button>
            <el-button
              v-if="memberConfig.allowUpdate"
              v-permission="['member:resetPwd']"
              type="warning" size="mini" icon="el-icon-key"
              @click="handleResetPwd(row)"
            >重置密码</el-button>
            <el-button
              type="primary" size="mini" icon="el-icon-shopping-bag-1"
              @click="openMemberOrderDialog(row)"
            >产品订单</el-button>
            <el-button
              v-if="!row.partner_id"
              type="warning" size="mini" icon="el-icon-link"
              @click="openBindPartnerDialog(row)"
            >绑定合伙人</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        :current-page="query.page"
        :page-sizes="[10, 15, 20, 50]"
        :page-size="query.pageSize"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        style="margin-top: 16px; text-align: right"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </el-card>

    <!-- 新增/编辑弹窗 -->
    <el-dialog :title="dialog.title" :visible.sync="dialog.visible" width="560px" @close="resetForm">
      <el-form ref="memberForm" :model="form" :rules="rules" label-width="80px">
        <!-- 头像上传 -->
        <el-form-item label="头像">
          <div class="avatar-upload-wrap">
            <el-avatar :size="72" :src="form.avatarPreview || form.avatar || defaultAvatar" class="avatar-preview" />
            <div class="avatar-upload-actions">
              <el-upload
                action="#"
                accept="image/*"
                :show-file-list="false"
                :before-upload="handleAvatarFile"
              >
                <el-button size="mini" icon="el-icon-upload2">选择图片</el-button>
              </el-upload>
              <span class="avatar-hint">支持 JPG/PNG，建议正方形</span>
            </div>
          </div>
        </el-form-item>

        <el-form-item label="账号" prop="username">
          <el-input v-model="form.username" :disabled="!!form.id" placeholder="请输入账号（6-20位）" />
        </el-form-item>
        <el-form-item :label="form.id ? '新密码' : '密码'" :prop="form.id ? '' : 'password'">
          <el-input v-model="form.password" type="password" :placeholder="form.id ? '不填则不修改密码' : '请输入密码（至少6位）'" show-password />
        </el-form-item>
        <el-form-item label="手机号" prop="phone">
          <el-input v-model="form.phone" placeholder="请输入手机号" maxlength="11" />
        </el-form-item>
        <el-form-item label="昵称" prop="nickname">
          <el-input v-model="form.nickname" placeholder="请输入昵称" />
        </el-form-item>
        <el-row v-if="memberConfig.showBalance || memberConfig.showPoints" :gutter="16">
          <el-col v-if="memberConfig.showBalance" :span="memberConfig.showPoints ? 12 : 24">
            <el-form-item label="余额">
              <el-input-number v-model="form.balance" :min="0" :precision="2" :step="10" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col v-if="memberConfig.showPoints" :span="memberConfig.showBalance ? 12 : 24">
            <el-form-item label="积分">
              <el-input-number v-model="form.points" :min="0" :step="100" style="width: 100%" />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
      <div slot="footer">
        <el-button size="mini" @click="dialog.visible = false">取消</el-button>
        <el-button type="primary" size="mini" :loading="dialog.loading" @click="handleSubmit">确定</el-button>
      </div>
    </el-dialog>

    <!-- 重置密码弹窗 -->
    <el-dialog title="重置密码" :visible.sync="pwdDialog.visible" width="400px" @close="pwdForm.password = ''">
      <el-form ref="pwdForm" :model="pwdForm" :rules="pwdRules" label-width="80px">
        <p class="reset-pwd-tip">正在为用户 <strong>{{ pwdDialog.username }}</strong> 重置密码</p>
        <el-form-item label="新密码" prop="password">
          <el-input v-model="pwdForm.password" type="password" placeholder="请输入新密码（至少6位）" show-password />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="mini" @click="pwdDialog.visible = false">取消</el-button>
        <el-button type="primary" size="mini" :loading="pwdDialog.loading" @click="handlePwdSubmit">确定重置</el-button>
      </div>
    </el-dialog>
    <!-- 余额明细弹窗 -->
    <el-dialog :title="`余额明细 — ${balanceLogDialog.username}`" :visible.sync="balanceLogDialog.visible" width="720px">
      <div class="log-summary" v-if="balanceLogDialog.visible">
        <el-button size="mini" type="success" icon="el-icon-plus" v-permission="['member:balance']" @click="openBalanceAdj">充值 / 扣除</el-button>
      </div>
      <el-table :data="balanceLogDialog.list" v-loading="balanceLogDialog.loading" border size="small" style="width:100%;margin-top:8px">
        <el-table-column prop="created_at" label="时间" width="155" />
        <el-table-column label="变动金额" width="110" align="right">
          <template slot-scope="{ row }">
            <span :class="row.amount > 0 ? 'amount-plus' : 'amount-minus'">
              {{ row.amount > 0 ? '+' : '' }}{{ Number(row.amount).toFixed(2) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="变动前" width="100" align="right">
          <template slot-scope="{ row }">¥ {{ Number(row.before).toFixed(2) }}</template>
        </el-table-column>
        <el-table-column label="变动后" width="100" align="right">
          <template slot-scope="{ row }">¥ {{ Number(row.after).toFixed(2) }}</template>
        </el-table-column>
        <el-table-column label="类型" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.type <= 2 ? (row.type === 1 ? 'success' : 'danger') : 'info'" size="small">
              {{ { 1:'后台充值', 2:'后台扣除', 3:'消费', 4:'退款' }[row.type] }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="120" show-overflow-tooltip />
        <el-table-column prop="operator" label="操作人" width="100" />
      </el-table>
      <el-pagination
        :current-page="balanceLogDialog.page" :page-size="balanceLogDialog.pageSize"
        :total="balanceLogDialog.total" layout="total, prev, pager, next"
        style="margin-top:12px;text-align:right"
        @current-change="(p) => { balanceLogDialog.page = p; fetchBalanceLogs() }"
      />
    </el-dialog>

    <!-- 积分明细弹窗 -->
    <el-dialog :title="`积分明细 — ${pointsLogDialog.username}`" :visible.sync="pointsLogDialog.visible" width="720px">
      <div class="log-summary" v-if="pointsLogDialog.visible">
        <el-button size="mini" type="primary" icon="el-icon-plus" v-permission="['member:points']" @click="openPointsAdj">充入 / 扣除</el-button>
      </div>
      <el-table :data="pointsLogDialog.list" v-loading="pointsLogDialog.loading" border size="small" style="width:100%;margin-top:8px">
        <el-table-column prop="created_at" label="时间" width="155" />
        <el-table-column label="变动积分" width="100" align="right">
          <template slot-scope="{ row }">
            <span :class="row.points > 0 ? 'amount-plus' : 'amount-minus'">
              {{ row.points > 0 ? '+' : '' }}{{ row.points }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="before" label="变动前" width="90" align="right" />
        <el-table-column prop="after" label="变动后" width="90" align="right" />
        <el-table-column label="类型" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.type <= 2 ? (row.type === 1 ? 'success' : 'danger') : 'info'" size="small">
              {{ { 1:'后台充入', 2:'后台扣除', 3:'消费', 4:'奖励' }[row.type] }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="120" show-overflow-tooltip />
        <el-table-column prop="operator" label="操作人" width="100" />
      </el-table>
      <el-pagination
        :current-page="pointsLogDialog.page" :page-size="pointsLogDialog.pageSize"
        :total="pointsLogDialog.total" layout="total, prev, pager, next"
        style="margin-top:12px;text-align:right"
        @current-change="(p) => { pointsLogDialog.page = p; fetchPointsLogs() }"
      />
    </el-dialog>

    <!-- 余额调整弹窗 -->
    <el-dialog title="余额调整" :visible.sync="balanceAdjDialog.visible" width="400px" append-to-body @close="$refs.balanceAdjForm && $refs.balanceAdjForm.resetFields()">
      <p class="adj-tip">用户：<strong>{{ balanceAdjDialog.username }}</strong>&nbsp;&nbsp;当前余额：<strong class="balance-text">¥ {{ Number(balanceAdjDialog.balance).toFixed(2) }}</strong></p>
      <el-form ref="balanceAdjForm" :model="balanceAdjForm" :rules="balanceAdjRules" label-width="80px">
        <el-form-item label="调整金额" prop="amount">
          <el-input-number v-model="balanceAdjForm.amount" :step="10" :precision="2" style="width:100%" placeholder="正数为充值，负数为扣除" />
          <div class="adj-hint">正数充值，负数扣除（如 -50 表示扣除 50 元）</div>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="balanceAdjForm.remark" placeholder="请输入备注（可选）" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="mini" @click="balanceAdjDialog.visible = false">取消</el-button>
        <el-button type="primary" size="mini" :loading="balanceAdjDialog.loading" @click="submitBalanceAdj">确定</el-button>
      </div>
    </el-dialog>

    <!-- 产品订单弹窗 -->
    <el-dialog
      :title="`${memberOrderDialog.username} 的产品订单`"
      :visible.sync="memberOrderDialog.visible"
      width="960px"
      @close="resetMemberOrderDialog"
    >
      <div class="dialog-search-bar">
        <el-input
          v-model="memberOrderDialog.keyword"
          placeholder="搜索订单号"
          clearable
          style="width:200px"
          @keyup.enter.native="searchMemberOrders"
        />
        <el-select v-model="memberOrderDialog.status" placeholder="订单状态" clearable style="width:120px">
          <el-option label="待付款" :value="0" />
          <el-option label="已付款" :value="1" />
          <el-option label="部分退款" :value="2" />
          <el-option label="已取消" :value="3" />
          <el-option label="已退款" :value="4" />
          <el-option label="已完成" :value="5" />
        </el-select>
        <el-button type="primary" size="mini" icon="el-icon-search" @click="searchMemberOrders">查询</el-button>
        <el-button size="mini" @click="memberOrderDialog.keyword = ''; memberOrderDialog.status = ''; searchMemberOrders()">重置</el-button>
      </div>
      <el-table
        :data="memberOrderDialog.list"
        v-loading="memberOrderDialog.loading"
        border
        stripe
        style="width:100%;margin-top:10px"
      >
        <el-table-column prop="order_no" label="订单号" min-width="180" show-overflow-tooltip />
        <el-table-column prop="product_name" label="产品名称" min-width="140" show-overflow-tooltip />
        <el-table-column prop="quantity" label="数量" width="70" align="center" />
        <el-table-column label="实付金额" width="110" align="right">
          <template slot-scope="{ row }">
            <span class="price-text">¥{{ Number(row.pay_amount).toFixed(2) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="productOrderStatusType(row.status)" size="small">
              {{ row.status_label || productOrderStatusLabel(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="下单时间" width="160" />
      </el-table>
      <el-pagination
        :current-page="memberOrderDialog.page"
        :page-sizes="[10, 15, 20]"
        :page-size="memberOrderDialog.pageSize"
        :total="memberOrderDialog.total"
        layout="total, sizes, prev, pager, next, jumper"
        style="margin-top:14px;text-align:right"
        @size-change="val => { memberOrderDialog.pageSize = val; memberOrderDialog.page = 1; fetchMemberOrders() }"
        @current-change="val => { memberOrderDialog.page = val; fetchMemberOrders() }"
      />
    </el-dialog>

    <!-- 绑定合伙人弹窗 -->
    <el-dialog
      :title="`为「${bindPartnerDialog.memberName}」绑定合伙人`"
      :visible.sync="bindPartnerDialog.visible"
      width="860px"
      @close="resetBindPartnerDialog"
    >
      <div class="dialog-search-bar">
        <el-input
          v-model="bindPartnerDialog.keyword"
          placeholder="合伙人姓名或手机号"
          clearable
          style="width:220px"
          @keyup.enter.native="searchBindPartner"
        />
        <el-button type="primary" size="mini" icon="el-icon-search" @click="searchBindPartner">查询</el-button>
        <el-button size="mini" @click="bindPartnerDialog.keyword = ''; searchBindPartner()">重置</el-button>
      </div>
      <el-table
        :data="bindPartnerDialog.list"
        v-loading="bindPartnerDialog.loading"
        border
        stripe
        style="width:100%;margin-top:10px"
      >
        <el-table-column prop="id" label="ID" width="70" align="center" />
        <el-table-column prop="name" label="姓名" min-width="120" show-overflow-tooltip />
        <el-table-column prop="phone" label="手机号" width="140" />
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="{ row }">
            <el-tag :type="row.review_status === 1 ? 'success' : ''" size="small">
              {{ { 0: '未审核', 1: '已通过' }[row.review_status] || '—' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100" align="center" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              type="primary" size="mini"
              @click="confirmBindPartner(row)"
            >绑定</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        :current-page="bindPartnerDialog.page"
        :page-sizes="[10, 15, 20]"
        :page-size="bindPartnerDialog.pageSize"
        :total="bindPartnerDialog.total"
        layout="total, sizes, prev, pager, next, jumper"
        style="margin-top:14px;text-align:right"
        @size-change="val => { bindPartnerDialog.pageSize = val; bindPartnerDialog.page = 1; fetchBindPartnerList() }"
        @current-change="val => { bindPartnerDialog.page = val; fetchBindPartnerList() }"
      />
    </el-dialog>

    <!-- 积分调整弹窗 -->
    <el-dialog title="积分调整" :visible.sync="pointsAdjDialog.visible" width="400px" append-to-body @close="$refs.pointsAdjForm && $refs.pointsAdjForm.resetFields()">
      <p class="adj-tip">用户：<strong>{{ pointsAdjDialog.username }}</strong>&nbsp;&nbsp;当前积分：<strong class="points-text">{{ pointsAdjDialog.points }}</strong></p>
      <el-form ref="pointsAdjForm" :model="pointsAdjForm" :rules="pointsAdjRules" label-width="80px">
        <el-form-item label="调整积分" prop="points">
          <el-input-number v-model="pointsAdjForm.points" :step="100" style="width:100%" placeholder="正数为充入，负数为扣除" />
          <div class="adj-hint">正数充入，负数扣除（如 -100 表示扣除 100 积分）</div>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="pointsAdjForm.remark" placeholder="请输入备注（可选）" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="mini" @click="pointsAdjDialog.visible = false">取消</el-button>
        <el-button type="primary" size="mini" :loading="pointsAdjDialog.loading" @click="submitPointsAdj">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getMemberConfig, getMemberList, createMember, updateMember,
  deleteMember, getMemberDetail, resetMemberPassword, uploadMemberAvatar,
  getMemberBalanceLogs, getMemberPointsLogs, adjustMemberBalance, adjustMemberPoints,
  bindMemberPartner
} from '@/api/member'
import { getUserProductOrders } from '@/api/order'
import { getPartnerList } from '@/api/partner'

export default {
  name: 'MemberManage',
  data() {
    return {
      // 从后端读取的模块配置
      memberConfig: {
        allowCreate: false,
        allowUpdate: true,
        showBalance: true,
        showPoints: true
      },
      defaultAvatar: 'https://cube.elemecdn.com/0/88/03b0d39583f48206768a7534e55bcpng.png',
      loading: false,
      tableData: [],
      total: 0,
      query: { page: 1, pageSize: 15, keyword: '', status: '', pending_coupon_issue: false },
      dialog: { visible: false, title: '', loading: false },
      form: {
        id: null, username: '', password: '', phone: '', nickname: '',
        avatar: '', avatarPreview: '', avatarBase64: '',
        balance: 0, points: 0, status: 1
      },
      rules: {
        username: [
          { required: true, message: '账号不能为空', trigger: 'blur' },
          { min: 3, max: 20, message: '账号长度 3-20 位', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '密码不能为空', trigger: 'blur' },
          { min: 6, message: '密码至少6位', trigger: 'blur' }
        ],
        nickname: [{ required: true, message: '昵称不能为空', trigger: 'blur' }],
        phone: [
          { required: true, message: '手机号不能为空', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号', trigger: 'blur' }
        ]
      },
      pwdDialog: { visible: false, loading: false, id: null, username: '' },
      pwdForm: { password: '' },
      pwdRules: {
        password: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '密码至少6位', trigger: 'blur' }
        ]
      },
      // 余额明细弹窗
      balanceLogDialog: {
        visible: false, username: '', userId: null,
        loading: false, list: [], total: 0, page: 1, pageSize: 10
      },
      // 积分明细弹窗
      pointsLogDialog: {
        visible: false, username: '', userId: null,
        loading: false, list: [], total: 0, page: 1, pageSize: 10
      },
      // 余额调整弹窗
      balanceAdjDialog: { visible: false, loading: false, id: null, username: '', balance: 0 },
      balanceAdjForm: { amount: 0, remark: '' },
      balanceAdjRules: {
        amount: [{ required: true, message: '请输入调整金额', trigger: 'blur' },
                 { validator: (r, v, cb) => v === 0 ? cb(new Error('金额不能为 0')) : cb(), trigger: 'blur' }]
      },
      // 产品订单弹窗
      memberOrderDialog: {
        visible: false, loading: false,
        userId: null, username: '',
        keyword: '', status: '',
        list: [], total: 0, page: 1, pageSize: 15
      },
      // 绑定合伙人弹窗
      bindPartnerDialog: {
        visible: false, loading: false, bindLoading: false,
        memberId: null, memberName: '',
        keyword: '',
        list: [], total: 0, page: 1, pageSize: 15
      },
      // 积分调整弹窗
      pointsAdjDialog: { visible: false, loading: false, id: null, username: '', points: 0 },
      pointsAdjForm: { points: 0, remark: '' },
      pointsAdjRules: {
        points: [{ required: true, message: '请输入调整积分', trigger: 'blur' },
                 { validator: (r, v, cb) => v === 0 ? cb(new Error('积分不能为 0')) : cb(), trigger: 'blur' }]
      }
    }
  },
  created() {
    this.applyRouteQuery()
    this.fetchConfig()
    this.fetchData()
  },
  activated() {
    this.applyRouteQuery()
    this.fetchData()
  },
  methods: {
    applyRouteQuery() {
      if (this.$route.query.pending_coupon_issue === '1') {
        this.query.pending_coupon_issue = true
      }
    },

    async fetchConfig() {
      try {
        const res = await getMemberConfig()
        this.memberConfig = Object.assign({}, this.memberConfig, res.data || {})
        this.$nextTick(() => this.$refs.table?.doLayout?.())
      } catch {}
    },

    async fetchData() {
      this.loading = true
      try {
        const params = { ...this.query }
        if (params.status === '') delete params.status
        if (!params.pending_coupon_issue) delete params.pending_coupon_issue
        const res = await getMemberList(params)
        this.tableData = res.data.list
        this.total = res.data.total
        this.$nextTick(() => this.$refs.table?.doLayout?.())
      } finally {
        this.loading = false
      }
    },
    handleSearch() { this.query.page = 1; this.fetchData() },
    handleReset() {
      this.query = { page: 1, pageSize: 15, keyword: '', status: '', pending_coupon_issue: false }
      this.fetchData()
    },
    handleSizeChange(val) { this.query.pageSize = val; this.fetchData() },
    handleCurrentChange(val) { this.query.page = val; this.fetchData() },

    handleCreate() {
      if (!this.memberConfig.allowCreate) {
        this.$message.warning('当前配置不允许新增用户')
        return
      }
      this.form = {
        id: null, username: '', password: '', phone: '', nickname: '',
        avatar: '', avatarPreview: '', avatarBase64: '',
        balance: 0, points: 0, status: 1
      }
      this.dialog = { visible: true, title: '新增用户', loading: false }
    },

    async handleEdit(row) {
      if (!this.memberConfig.allowUpdate) {
        this.$message.warning('当前配置不允许编辑用户')
        return
      }
      const res = await getMemberDetail({ id: row.id })
      const d = res.data
      this.form = {
        id: d.id, username: d.username, password: '', phone: d.phone || '', nickname: d.nickname,
        avatar: d.avatar || '', avatarPreview: '', avatarBase64: '',
        balance: d.balance || 0, points: d.points || 0, status: d.status
      }
      this.dialog = { visible: true, title: '编辑用户', loading: false }
    },

    async handleDelete(row) {
      try {
        await this.$confirm(`确认删除用户「${row.username}」？此操作不可恢复`, '提示', { type: 'warning' })
        await deleteMember({ id: row.id })
        this.$message.success('删除成功')
        this.fetchData()
      } catch {}
    },

    handleResetPwd(row) {
      this.pwdDialog = { visible: true, loading: false, id: row.id, username: row.username }
      this.pwdForm.password = ''
    },

    async handlePwdSubmit() {
      this.$refs.pwdForm.validate(async valid => {
        if (!valid) return
        this.pwdDialog.loading = true
        try {
          await resetMemberPassword({ id: this.pwdDialog.id, password: this.pwdForm.password })
          this.$message.success('密码重置成功')
          this.pwdDialog.visible = false
        } finally {
          this.pwdDialog.loading = false
        }
      })
    },

    handleAvatarFile(file) {
      const isImage = file.type.startsWith('image/')
      const isLt2M = file.size / 1024 / 1024 < 2
      if (!isImage) { this.$message.error('请选择图片文件'); return false }
      if (!isLt2M) { this.$message.error('图片大小不能超过 2MB'); return false }

      const reader = new FileReader()
      reader.onload = e => {
        this.form.avatarPreview = e.target.result
        this.form.avatarBase64 = e.target.result
      }
      reader.readAsDataURL(file)
      return false
    },

    handleSubmit() {
      this.$refs.memberForm.validate(async valid => {
        if (!valid) return
        this.dialog.loading = true
        try {
          if (this.form.id) {
            // 先上传头像（如果有新图）
            if (this.form.avatarBase64) {
              const avatarRes = await uploadMemberAvatar({ id: this.form.id, avatar: this.form.avatarBase64 })
              this.form.avatar = avatarRes.data.avatar
            }
            await updateMember({
              id: this.form.id, nickname: this.form.nickname,
              phone: this.form.phone || null,
              password: this.form.password || undefined,
              balance: this.form.balance, points: this.form.points,
              status: this.form.status
            })
            this.$message.success('更新成功')
          } else {
            const payload = {
              username: this.form.username, password: this.form.password,
              phone: this.form.phone || undefined,
              nickname: this.form.nickname, balance: this.form.balance,
              points: this.form.points, status: this.form.status
            }
            const res = await createMember(payload)
            // 新建后如果有头像，立刻上传
            if (this.form.avatarBase64 && res.data && res.data.id) {
              await uploadMemberAvatar({ id: res.data.id, avatar: this.form.avatarBase64 })
            }
            this.$message.success('创建成功')
          }
          this.dialog.visible = false
          this.fetchData()
        } finally {
          this.dialog.loading = false
        }
      })
    },

    resetForm() {
      this.$refs.memberForm && this.$refs.memberForm.resetFields()
    },

    // ── 余额 ──────────────────────────────
    handleBalance(row) {
      this.balanceLogDialog = {
        visible: true, username: row.username, userId: row.id,
        loading: false, list: [], total: 0, page: 1, pageSize: 10
      }
      this.fetchBalanceLogs()
    },
    async fetchBalanceLogs() {
      this.balanceLogDialog.loading = true
      try {
        const res = await getMemberBalanceLogs({
          id: this.balanceLogDialog.userId,
          page: this.balanceLogDialog.page,
          pageSize: this.balanceLogDialog.pageSize
        })
        this.balanceLogDialog.list  = res.data.list
        this.balanceLogDialog.total = res.data.total
      } finally {
        this.balanceLogDialog.loading = false
      }
    },
    openBalanceAdj() {
      const row = this.tableData.find(r => r.id === this.balanceLogDialog.userId) || {}
      this.balanceAdjDialog = {
        visible: true, loading: false,
        id: this.balanceLogDialog.userId,
        username: this.balanceLogDialog.username,
        balance: row.balance || 0
      }
      this.balanceAdjForm = { amount: 0, remark: '' }
    },
    async submitBalanceAdj() {
      this.$refs.balanceAdjForm.validate(async valid => {
        if (!valid) return
        this.balanceAdjDialog.loading = true
        try {
          await adjustMemberBalance({
            id: this.balanceAdjDialog.id,
            amount: this.balanceAdjForm.amount,
            remark: this.balanceAdjForm.remark
          })
          this.$message.success('余额调整成功')
          this.balanceAdjDialog.visible = false
          this.fetchData()
          this.fetchBalanceLogs()
        } finally {
          this.balanceAdjDialog.loading = false
        }
      })
    },

    // ── 产品订单 ──────────────────────────
    openMemberOrderDialog(row) {
      this.memberOrderDialog.userId   = row.id
      this.memberOrderDialog.username = row.nickname || row.username || row.phone
      this.memberOrderDialog.keyword  = ''
      this.memberOrderDialog.status   = ''
      this.memberOrderDialog.page     = 1
      this.memberOrderDialog.pageSize = 15
      this.memberOrderDialog.visible  = true
      this.fetchMemberOrders()
    },
    resetMemberOrderDialog() {
      this.memberOrderDialog.list  = []
      this.memberOrderDialog.total = 0
      this.memberOrderDialog.page  = 1
    },
    searchMemberOrders() {
      this.memberOrderDialog.page = 1
      this.fetchMemberOrders()
    },
    async fetchMemberOrders() {
      this.memberOrderDialog.loading = true
      try {
        const params = {
          user_id:  this.memberOrderDialog.userId,
          page:     this.memberOrderDialog.page,
          pageSize: this.memberOrderDialog.pageSize
        }
        if (this.memberOrderDialog.keyword) params.order_no = this.memberOrderDialog.keyword
        if (this.memberOrderDialog.status !== '') params.status = this.memberOrderDialog.status
        const res = await getUserProductOrders(params)
        const d = res.data || {}
        this.memberOrderDialog.list  = d.list || []
        this.memberOrderDialog.total = d.total || 0
      } finally {
        this.memberOrderDialog.loading = false
      }
    },
    productOrderStatusLabel(s) {
      return { 0: '待付款', 1: '已付款', 2: '部分退款', 3: '已取消', 4: '已退款', 5: '已完成' }[s] ?? String(s)
    },
    productOrderStatusType(s) {
      return { 0: 'info', 1: 'success', 2: 'warning', 3: '', 4: 'danger', 5: 'success' }[s] ?? ''
    },

    // ── 积分 ──────────────────────────────
    handlePoints(row) {
      this.pointsLogDialog = {
        visible: true, username: row.username, userId: row.id,
        loading: false, list: [], total: 0, page: 1, pageSize: 10
      }
      this.fetchPointsLogs()
    },
    async fetchPointsLogs() {
      this.pointsLogDialog.loading = true
      try {
        const res = await getMemberPointsLogs({
          id: this.pointsLogDialog.userId,
          page: this.pointsLogDialog.page,
          pageSize: this.pointsLogDialog.pageSize
        })
        this.pointsLogDialog.list  = res.data.list
        this.pointsLogDialog.total = res.data.total
      } finally {
        this.pointsLogDialog.loading = false
      }
    },
    openPointsAdj() {
      const row = this.tableData.find(r => r.id === this.pointsLogDialog.userId) || {}
      this.pointsAdjDialog = {
        visible: true, loading: false,
        id: this.pointsLogDialog.userId,
        username: this.pointsLogDialog.username,
        points: row.points || 0
      }
      this.pointsAdjForm = { points: 0, remark: '' }
    },
    async submitPointsAdj() {
      this.$refs.pointsAdjForm.validate(async valid => {
        if (!valid) return
        this.pointsAdjDialog.loading = true
        try {
          await adjustMemberPoints({
            id: this.pointsAdjDialog.id,
            points: this.pointsAdjForm.points,
            remark: this.pointsAdjForm.remark
          })
          this.$message.success('积分调整成功')
          this.pointsAdjDialog.visible = false
          this.fetchData()
          this.fetchPointsLogs()
        } finally {
          this.pointsAdjDialog.loading = false
        }
      })
    },

    // ── 绑定合伙人 ────────────────────────
    openBindPartnerDialog(row) {
      this.bindPartnerDialog.memberId   = row.id
      this.bindPartnerDialog.memberName = row.nickname || row.username || row.phone
      this.bindPartnerDialog.keyword    = ''
      this.bindPartnerDialog.page       = 1
      this.bindPartnerDialog.pageSize   = 15
      this.bindPartnerDialog.list       = []
      this.bindPartnerDialog.total      = 0
      this.bindPartnerDialog.visible    = true
      this.fetchBindPartnerList()
    },
    resetBindPartnerDialog() {
      this.bindPartnerDialog.list  = []
      this.bindPartnerDialog.total = 0
      this.bindPartnerDialog.page  = 1
    },
    searchBindPartner() {
      this.bindPartnerDialog.page = 1
      this.fetchBindPartnerList()
    },
    async fetchBindPartnerList() {
      this.bindPartnerDialog.loading = true
      try {
        const params = {
          page:          this.bindPartnerDialog.page,
          pageSize:      this.bindPartnerDialog.pageSize,
          hide_rejected: 1
        }
        if (this.bindPartnerDialog.keyword) params.keyword = this.bindPartnerDialog.keyword
        const res = await getPartnerList(params)
        const d = res.data || {}
        this.bindPartnerDialog.list  = d.list || []
        this.bindPartnerDialog.total = d.total || 0
      } finally {
        this.bindPartnerDialog.loading = false
      }
    },
    async confirmBindPartner(partnerRow) {
      const memberName  = this.bindPartnerDialog.memberName
      const partnerName = partnerRow.name || partnerRow.phone
      try {
        await this.$confirm(
          `确认将用户「${memberName}」与合伙人「${partnerName}」进行绑定？`,
          '绑定确认',
          { type: 'warning', confirmButtonText: '确定绑定', cancelButtonText: '取消' }
        )
      } catch {
        return
      }
      try {
        await bindMemberPartner({
          user_id:    this.bindPartnerDialog.memberId,
          partner_id: partnerRow.id
        })
        this.$message.success('绑定成功')
        this.bindPartnerDialog.visible = false
        this.fetchData()
      } catch (e) {
        this.$message.error(e?.message || '绑定失败')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.balance-text { color: #e6a23c; font-weight: 600; }
.points-text  { color: #409eff; font-weight: 600; }
.amount-plus  { color: #67c23a; font-weight: 600; }
.amount-minus { color: #f56c6c; font-weight: 600; }
.log-summary  { display: flex; justify-content: flex-end; }
.adj-tip      { margin: 0 0 16px; color: #606266; font-size: 13px; }
.adj-hint     { font-size: 12px; color: #909399; margin-top: 4px; }

.avatar-upload-wrap {
  display: flex;
  align-items: center;
  gap: 16px;
  .avatar-preview { flex-shrink: 0; border: 1px solid #e4e7ed; }
  .avatar-upload-actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
    .avatar-hint { font-size: 12px; color: #909399; }
  }
}

.reset-pwd-tip {
  margin: 0 0 16px;
  color: #606266;
  font-size: 13px;
}

.dialog-search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.price-text { color: #f56c6c; font-weight: 600; }
.text-muted { color: #c0c4cc; font-size: 12px; }

.partner-info {
  display: flex;
  flex-direction: column;
  line-height: 1.5;
  .partner-name  { font-weight: 600; color: #303133; font-size: 13px; }
  .partner-phone { color: #909399; font-size: 12px; }
}
</style>
